import React from "react";
import PropTypes from "prop-types";

/**
 * Daily-schedule row chips — the seven 18×18 marks from "Card · Daily
 * schedule (typical weekday)" (3578:2976).
 *
 * The board's Dev Mode server never issued asset hashes for these (its
 * get_design_context was down for the whole capture), so there was no SVG to
 * download. Each glyph below is redrawn from a 5× native-resolution render of
 * the chip, keeping the board's stroked-line style: ~1.5px strokes, round
 * caps and joins, on an 18×18 box. Colours come from the chip palette in
 * CHIPS, not from the paths, so every glyph strokes with currentColor.
 */

const S = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

/** Sun/asterisk — the board's generic mark ("Wake up", "Other"). */
export const SunIcon = () => (
  <svg viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <circle cx="9" cy="9" r="3" {...S} />
    <path d="M9 1.6V3M9 15v1.4M1.6 9H3M15 9h1.4" {...S} />
  </svg>
);

/** Mug with a side handle — drinks / pre-meal. */
export const CupIcon = () => (
  <svg viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <rect x="2.2" y="5.2" width="9.6" height="7.6" rx="1.6" {...S} />
    <path d="M11.8 7.2h1.9a2.1 2.1 0 0 1 0 4.2h-1.9" {...S} />
  </svg>
);

/** Concentric bullseye — physical activity / yoga / exercise. */
export const ActivityIcon = () => (
  <svg viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <circle cx="9" cy="9" r="6.6" {...S} />
    <circle cx="9" cy="9" r="3.4" {...S} />
    <circle cx="9" cy="9" r="1.2" fill="currentColor" stroke="none" />
  </svg>
);

/** Plate — a circle bisected by a horizontal line. Meals. */
export const PlateIcon = () => (
  <svg viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <circle cx="9" cy="9" r="6.4" {...S} />
    <path d="M2.6 8.2h12.8" {...S} />
  </svg>
);

/** Capsule with a divider — medicine. */
export const PillIcon = () => (
  <svg viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <rect x="2.4" y="6.2" width="13.2" height="5.6" rx="2.8" {...S} />
    <path d="M9 6.2v5.6" {...S} />
  </svg>
);

/** Leaf plus two berries — snacks / fruit. */
export const FruitIcon = () => (
  <svg viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path d="M2.2 6.4h5.2L4.8 10z" fill="currentColor" stroke="none" />
    <circle cx="10.4" cy="9.4" r="2.1" {...S} />
    <circle cx="15.1" cy="9.4" r="2.1" {...S} />
  </svg>
);

/** Solid crescent — sleep. Filled, not stroked (white on the near-black chip). */
export const MoonIcon = () => (
  <svg viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path
      d="M14.2 11.3A5.9 5.9 0 0 1 6.7 3.8a5.9 5.9 0 1 0 7.5 7.5z"
      fill="currentColor"
      stroke="none"
    />
  </svg>
);

/**
 * The chip palette, sampled 1:1 off the board. `bg` is the 36×36 chip fill,
 * `fg` tints the glyph.
 */
export const CHIPS = {
  sun: { bg: "#f3f4f6", fg: "#4a5565", Icon: SunIcon },
  cup: { bg: "#fef3c7", fg: "#92400e", Icon: CupIcon },
  activity: { bg: "#efedfe", fg: "#4236ad", Icon: ActivityIcon },
  plate: { bg: "#fffbeb", fg: "#92400e", Icon: PlateIcon },
  pill: { bg: "#d1fae5", fg: "#047857", Icon: PillIcon },
  fruit: { bg: "#d1fae5", fg: "#047857", Icon: FruitIcon },
  moon: { bg: "#101828", fg: "#ffffff", Icon: MoonIcon },
};

/**
 * Activity text → chip.
 *
 * The wire carries no type: model.ScheduleEntry is {entryId, timeMinutes,
 * activity} with activity as free text, so the chip has to be inferred here.
 * This is presentation only — nothing is written back.
 *
 * A CUSTOM activity — anything the user types that matches no rule — gets the
 * board's "Other activity" chip (row 9, `3496:3025`): the indigo bullseye on
 * #efedfe. That is the same glyph the board gives "Physical activity / yoga"
 * (row 3) and "Yoga / exercise" (row 11), so it reads as the generic
 * something-happens-here mark rather than a wrong guess.
 *
 * ORDER IS LOAD-BEARING, and every rule below is anchored to a real row on
 * the board:
 *   • medicine before activity, so "Walk / medicine intake" is a pill and
 *     not the walk/activity bullseye;
 *   • snack before meal, so "Midday snack" is fruit rather than a plate;
 *   • meal before sleep, so "Bed time meal" stays a plate — "bed" alone must
 *     not make it a moon;
 *   • "Wake up" and a bare "Other" are pinned EXPLICITLY. They used to reach
 *     the sun chip by falling through to the default; now that the default is
 *     the bullseye, they need rules of their own or they would silently
 *     change glyph (board rows 1 and 12 are both the sun/clock mark).
 */
const RULES = [
  [/\b(medicine|medication|meds?|tablet|pill|dose|dosage)\b/i, "pill"],
  [/\b(snack|fruit|nuts|salad)\b/i, "fruit"],
  // Drinks BEFORE meals: "Pre-breakfast" is a cup on the board, and the
  // hyphen gives \bbreakfast\b a word boundary, so the meal rule would
  // otherwise claim it.
  [/(\bpre-?\s?breakfast\b|\b(tea|coffee|water|juice|drink|hydrat\w*)\b)/i, "cup"],
  [/\b(breakfast|lunch|dinner|supper|meal|brunch|food|eat)\b/i, "plate"],
  [/\b(sleep|nap|bed\s*time|bedtime|rest)\b/i, "moon"],
  [/\b(yoga|exercise|workout|walk|activity|gym|run|jog|stretch|physical)\b/i, "activity"],
  // Board rows 1 and 12. These sit AFTER the activity rule on purpose: row 9
  // is "Other activity", which must stay a bullseye rather than be claimed
  // here by the word "Other".
  [/^\s*wake([\s-]*up)?\s*$|\bwake[\s-]*up\b/i, "sun"],
  [/^\s*other\s*$/i, "sun"],
];

export const chipFor = (activity) => {
  const text = typeof activity === "string" ? activity : "";
  for (let i = 0; i < RULES.length; i += 1) {
    if (RULES[i][0].test(text)) return CHIPS[RULES[i][1]];
  }
  // Custom / unrecognised → the board's "Other activity" chip.
  return CHIPS.activity;
};

export default chipFor;

/* ── Section chrome (not chips) ─────────────────────────────────────── */

/** Clock — the medallion on the "No daily schedule yet" placeholder. 24-box
 *  with a 1.6 stroke like ccIcons.ChatBubbleIcon, which renders in the same
 *  56px .cc-empty-medallion. */
export const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" {...S} strokeWidth="1.6" />
    <path d="M12 7.2V12l3.2 2" {...S} strokeWidth="1.6" />
  </svg>
);

/** Calendar — the day-bar date control and the "Past schedules" control. */
export const CalendarIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <rect x="1.75" y="2.75" width="10.5" height="9.5" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
    <path d="M1.75 5.75h10.5M4.5 1.5v2.5M9.5 1.5v2.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

/** Chevron — day-bar prev/next arrows (left/right) and dropdown caret (down). */
const CHEVRON_PATHS = {
  left: "M8.5 2.5L4 7l4.5 4.5",
  right: "M5.5 2.5L10 7l-4.5 4.5",
  down: "M2.5 5.5L7 10l4.5-4.5",
};
export const Chevron = ({ dir }) => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path
      d={CHEVRON_PATHS[dir] || CHEVRON_PATHS.right}
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
Chevron.propTypes = { dir: PropTypes.oneOf(["left", "right", "down"]).isRequired };

/** Pencil — the banner's "edit this day" control (mock: orange, next to
 *  "Schedule for 10 Jan 2026"). Same glyph as AddressBookDialog's. */
export const PencilIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 20h9" {...S} strokeWidth="2" />
    <path d="M16.5 3.5a2.12 2.12 0 1 1 3 3L7 19l-4 1 1-4z" {...S} strokeWidth="2" />
  </svg>
);
